<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { searchPlaybook } from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaRemoteSelect, {
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaVaultUploadPanel from '@/components/design/domain/fileVault/GaiaVaultUploadPanel.vue';
import type { VaultUploadPanelApi } from '@/components/design/domain/fileVault/vaultUploadPanel';
import { playbookRunsRoute } from '@/router/links';

interface Props {
  files: File[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  done: [];
}>();

const message = useGaiaToast();
const router = useRouter();
const show = ref(true);
const selectedPlaybookUid = ref<string | null>(null);
const running = ref(false);
const uploadPanel = ref<VaultUploadPanelApi | null>(null);
const uploadReady = ref(false);

// Search only ACTIVE playbooks, mirroring the Intake Agent picker. The field
// owns the debounce, the options and the loading flag.
async function fetchPlaybooks(text: string): Promise<GaiaRemotePage> {
  const response = await searchPlaybook({
    body: { text, limit: 50, column_filters: { status: ['active'] } },
  });
  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to search playbooks'));
  }
  return {
    options: response.data.results.map(playbook => ({
      label: playbook.name,
      value: playbook.uid,
    })),
  };
}

function handleSearchError(error: unknown): void {
  logger.error('Error searching playbooks:', error);
}

function handleUpdateShow(value: boolean): void {
  show.value = value;
  if (!value && !running.value) {
    emit('done');
  }
}

async function handleRun(): Promise<void> {
  const playbookUid = selectedPlaybookUid.value;
  const panel = uploadPanel.value;
  if (!playbookUid || !panel) return;

  running.value = true;
  // The files land in the folder picked in the panel; a failed upload stays
  // listed there, with nothing run.
  const uploaded = await panel.upload();
  if (!uploaded) {
    running.value = false;
    return;
  }
  const revisionUids = uploaded.map(({ revision }) => revision.uid);

  const loading = message.loading('Starting playbook runs…', { duration: 0 });
  try {
    const runUids: string[] = [];
    let failed = 0;
    for (const revisionUid of revisionUids) {
      try {
        const summary = await PlaybooksService.startRun(playbookUid, {
          input_vault_file_revision_uid: revisionUid,
          // Empty: every configured action runs.
          skip_action_types: [],
        });
        runUids.push(summary.uid);
      } catch {
        failed += 1;
      }
    }

    if (!runUids.length) {
      message.error('Failed to start playbook runs.');
      return;
    }

    message.success(
      `Started ${runUids.length} playbook run${runUids.length === 1 ? '' : 's'}${
        failed > 0 ? ` (${failed} failed)` : ''
      }.`
    );

    show.value = false;
    if (runUids.length === 1) {
      await router.push({
        name: 'playbook-run-detail',
        params: { uid: playbookUid, runUid: runUids[0] },
      });
    } else {
      await router.push(playbookRunsRoute(playbookUid));
    }
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to start playbook runs.'));
  } finally {
    loading.destroy();
    running.value = false;
    emit('done');
  }
}

onMounted(() => {
  if (props.files.length === 0) {
    emit('done');
  }
});
</script>

<template>
  <gaia-modal
    v-model:show="show"
    width="600px"
    :mask-closable="!running"
    :closable="!running"
    @update:show="handleUpdateShow"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">Run a playbook</gaia-text>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <gaia-form-field label="Playbook">
        <gaia-remote-select
          v-model:value="selectedPlaybookUid"
          clearable
          prime-on-mount
          placeholder="Search active playbooks"
          :debounce="400"
          :disabled="running"
          :fetch="fetchPlaybooks"
          @error="handleSearchError"
        />
      </gaia-form-field>
      <!-- One run per file listed here, each against the playbook above. -->
      <gaia-vault-upload-panel
        ref="uploadPanel"
        :active="show"
        selectable-destination
        :initial-files="props.files"
        :unpack-archives="false"
        @update:ready="uploadReady = $event"
      />
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button size="small" :disabled="running" @click="show = false">Cancel</gaia-button>
        <gaia-button
          size="small"
          type="primary"
          :disabled="!selectedPlaybookUid || !uploadReady"
          :loading="running"
          @click="handleRun"
        >
          Run
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
